/**
 * Single acceptance rule for authoritative game states.
 *
 * Every state source (REST fetch, initial_state, game_update, game_finished,
 * reconnect refresh, visibility-resume refresh) evaluates the incoming
 * `state_seq` against the last applied one BEFORE calling applyGameState:
 *
 *   - seq > lastApplied  → accept (and advance the baseline)
 *   - seq <= lastApplied → reject (stale or duplicate)
 *   - seq missing        → accept (legacy payload during rollout — the server
 *                          may not have deployed the sequence yet)
 *
 * The baseline starts at NO_STATE_APPLIED (-1), so the first state of a game
 * always establishes it. It is reset only when the scene (re)initializes for
 * a game — i.e. when switching to a genuinely different game or rebuilding
 * the scene, both of which must accept the current snapshot.
 */

export const NO_STATE_APPLIED = -1;

export interface StateGateVerdict {
  accepted: boolean;
  /** Reason code for diagnostics: 'newer' | 'baseline' | 'no-seq-legacy' | 'stale-or-duplicate' */
  reason: string;
  /** Baseline to store after this verdict (unchanged on reject). */
  nextLastApplied: number;
}

export function evaluateStateSeq(
  incoming: number | null | undefined,
  lastApplied: number,
): StateGateVerdict {
  if (incoming === null || incoming === undefined) {
    return { accepted: true, reason: 'no-seq-legacy', nextLastApplied: lastApplied };
  }
  if (incoming > lastApplied) {
    return {
      accepted: true,
      reason: lastApplied === NO_STATE_APPLIED ? 'baseline' : 'newer',
      nextLastApplied: incoming,
    };
  }
  return { accepted: false, reason: 'stale-or-duplicate', nextLastApplied: lastApplied };
}

/**
 * Guard for the visibility-resume refresh: fire only when the page just
 * became visible, the scene is alive, and no resume refresh is already
 * running. Pure so it is unit-testable.
 */
export function shouldTriggerResumeRefresh(ctx: {
  visibilityState: string;
  shutDown: boolean;
  refreshInFlight: boolean;
}): boolean {
  return ctx.visibilityState === 'visible' && !ctx.shutDown && !ctx.refreshInFlight;
}
